import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image, Platform, ScrollView } from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useDailyDecision } from '../context/DailyDecisionContext';
import { useCharacterPopup } from '../context/CharacterPopupContext';
import Modal from './Modal';
import AnimatedButton from './AnimatedButton';
import RSKCurrency from './RSKCurrency';
import { rskService } from '../services/rskService';
import { dailyDecisionService } from '../services/dailyDecisionService';

// Import character images
const tommySmiling = require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_Smiling_v01_SL.png');
const tommyGrimace = require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_Grimace_v01_SL.png');

const DailyDecisionPopup = () => {
  const { palette } = useTheme();
  const { user } = useAuth();
  const { showDailyDecision, todaysQuestion, closeDailyDecision, reviewMode, todaysAnswer, openReviewMode, completeDailyDecision } = useDailyDecision();
  const { showCelebration, showTip } = useCharacterPopup();
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [rskAwarded, setRskAwarded] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Reset state when modal opens with a new question (not when closing)
  useEffect(() => {
    if (showDailyDecision && todaysQuestion && !isClosing) {
      setSelectedAnswer(null);
      setShowResult(false);
      setRskAwarded(0);
      setProcessing(false);
    }
  }, [showDailyDecision, todaysQuestion?.id]);
  
  // Reset closing state when modal is fully closed
  useEffect(() => {
    if (!showDailyDecision) {
      setIsClosing(false);
    }
  }, [showDailyDecision]);

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
  };

  const handleSubmitAnswer = async () => {
    if (!selectedAnswer || !user || processing) return;

    setProcessing(true);
    
    // Submit answer and award RSK using context method
    const { success } = await completeDailyDecision(
      todaysQuestion.id,
      selectedAnswer.id,
      selectedAnswer.quality,
      selectedAnswer.rskReward
    );

    if (success) {
      setRskAwarded(selectedAnswer.rskReward);
      setShowResult(true);
      setProcessing(false);
      
      // Show celebration popup based on answer quality
      const quality = selectedAnswer.quality;
      if (quality === 'best') {
        showCelebration(
          "Perfect Choice! 🎯",
          `+${selectedAnswer.rskReward} RSK! That's the optimal decision. You're thinking like a millionaire!`
        );
      } else if (quality === 'good') {
        showTip(
          "Good Decision! 👍",
          `+${selectedAnswer.rskReward} RSK! Solid choice. Keep refining your decision-making skills.`
        );
      }
      // Don't auto-close - let user click Continue to close
    } else {
      console.error('Failed to submit daily decision');
      setProcessing(false);
    }
  };

  const handleClose = () => {
    // Set closing state to prevent any re-renders from showing the question again
    setIsClosing(true);
    
    // Clear local state
    setSelectedAnswer(null);
    setShowResult(false);
    setRskAwarded(0);
    setProcessing(false);
    
    // Close the modal via context
    closeDailyDecision();
  };

  const getCharacterImage = () => {
    // Only show character feedback after result is shown
    if (!showResult) return tommySmiling;
    
    // Show grimace for bad answers, smiling for good answers
    return rskAwarded < 0 ? tommyGrimace : tommySmiling;
  };

  const getRskColor = (amount) => {
    if (amount > 0) return '#10B981'; // Green for positive
    if (amount < 0) return '#EF4444'; // Red for negative
    return palette.text; // Default for zero
  };


  // Don't show if closing or not visible
  if (isClosing || !showDailyDecision) {
    return null;
  }
  
  // In review mode, we need todaysAnswer, otherwise we need todaysQuestion
  if (!reviewMode && !todaysQuestion) {
    return null;
  }
  
  if (reviewMode && !todaysAnswer) {
    return null;
  }

  // Use current question or review data
  const currentQuestion = reviewMode ? todaysAnswer?.question : todaysQuestion;
  const isReviewing = reviewMode && todaysAnswer;

  if (!currentQuestion) {
    return null;
  }

  return (
    <Modal 
      visible={showDailyDecision} 
      onClose={handleClose}
      showCloseButton={true}
      backgroundColor="white"
    >
      <ScrollView 
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={true}
      >
        <View style={styles.container}>
        {!showResult && !isReviewing ? (
          <>
            {/* Question Screen */}
            <View style={styles.header}>
              <Text style={[styles.title, { color: '#1F2937' }]}>
                Daily Decision 🎯
              </Text>
              <Text style={[styles.question, { color: '#374151' }]}>
                {currentQuestion.text}
              </Text>
            </View>

            <View style={styles.answersContainer}>
              {currentQuestion.answers.map((answer, index) => (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.answerOption,
                    selectedAnswer?.id === answer.id && styles.answerOptionSelected,
                  ]}
                  onPress={() => handleAnswerSelect(answer)}
                  disabled={processing}
                >
                  <View style={styles.answerHeader}>
                    <View style={[
                      styles.radioButton, 
                      { 
                        borderColor: selectedAnswer?.id === answer.id 
                          ? (palette.primary || '#FF0600') 
                          : '#D1D5DB' 
                      }
                    ]}>
                      {selectedAnswer?.id === answer.id && (
                        <View style={[
                          styles.radioButtonInner, 
                          { backgroundColor: palette.primary || '#FF0600' }
                        ]} />
                      )}
                    </View>
                    <View style={styles.answerTextContainer}>
                      <Text style={[styles.answerText, { color: '#1F2937' }]}>
                        {answer.text}
                      </Text>
                    </View>
                  </View>
                </TouchableOpacity>
              ))}
            </View>

            <View style={styles.actions}>
              <AnimatedButton
                title="Submit Answer"
                onPress={handleSubmitAnswer}
                disabled={!selectedAnswer || processing}
                loading={processing}
                style={[
                  styles.submitButton,
                  {
                    backgroundColor: selectedAnswer 
                      ? (palette.primary || '#FF0600') 
                      : '#E5E7EB',
                    opacity: selectedAnswer ? 1 : 0.5,
                  }
                ]}
                textStyle={styles.submitButtonText}
              />
            </View>
          </>
        ) : isReviewing ? (
          <>
            {/* Review Mode Screen */}
            <View style={styles.header}>
              <Text style={[styles.title, { color: '#1F2937' }]}>
                Daily Decision Review 📝
              </Text>
              <Text style={[styles.question, { color: '#374151' }]}>
                {currentQuestion.text}
              </Text>
            </View>

            <View style={styles.answersContainer}>
              {currentQuestion.answers.map((answer, index) => {
                const isSelected = answer.id === todaysAnswer.selectedAnswerId;
                const isCorrect = answer.quality === 'best' || answer.quality === 'better';
                const isIncorrect = answer.quality === 'bad' || answer.quality === 'worse' || answer.quality === 'worst';
                
                // Get answer quality label and color
                const getAnswerQuality = (quality) => {
                  switch(quality) {
                    case 'best': return { label: 'Best Choice', color: '#22C55E' };
                    case 'better': return { label: 'Better Choice', color: '#22C55E' };
                    case 'good': return { label: 'Good Choice', color: '#84CC16' };
                    case 'bad': return { label: 'Bad Choice', color: '#EF4444' };
                    case 'worse': return { label: 'Worse Choice', color: '#EF4444' };
                    case 'worst': return { label: 'Worst Choice', color: '#DC2626' };
                    default: return { label: 'Choice', color: '#6B7280' };
                  }
                };
                
                const quality = getAnswerQuality(answer.quality);
                
                return (
                  <View
                    key={index}
                    style={[
                      styles.answerOption,
                      {
                        borderColor: isSelected 
                          ? (isCorrect ? '#22C55E' : '#EF4444')
                          : quality.color,
                        backgroundColor: isSelected 
                          ? (isCorrect ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)')
                          : `${quality.color}15`, // Add transparency
                        opacity: 1, // Show all answers clearly
                      }
                    ]}
                  >
                    <View style={styles.answerHeader}>
                      <View style={[
                        styles.reviewIndicator,
                        {
                          backgroundColor: isSelected 
                            ? (isCorrect ? '#22C55E' : '#EF4444')
                            : quality.color,
                          borderWidth: 1,
                          borderColor: isSelected 
                            ? (isCorrect ? '#16A34A' : '#DC2626')
                            : quality.color
                        }
                      ]}>
                        <Text style={styles.reviewIndicatorText}>
                          {isSelected ? (isCorrect ? '✓' : '✗') : (isCorrect ? '✓' : isIncorrect ? '✗' : '○')}
                        </Text>
                      </View>
                      <View style={styles.answerTextContainer}>
                        <Text style={[styles.answerText, { color: '#1F2937' }]}>
                          {answer.text}
                        </Text>
                        <View style={styles.reviewLabelsContainer}>
                          <Text style={[styles.reviewLabel, { color: quality.color }]}>
                            {quality.label} ({answer.rsk > 0 ? '+' : ''}{answer.rsk} RSK)
                          </Text>
                          {isSelected && (
                            <Text style={[styles.selectedLabel, { color: isCorrect ? '#22C55E' : '#EF4444' }]}>
                              Your Choice
                            </Text>
                          )}
                        </View>
                      </View>
                    </View>
                  </View>
                );
              })}
            </View>

            <View style={styles.actions}>
              <AnimatedButton
                title="Close Review"
                onPress={handleClose}
                style={[
                  styles.submitButton,
                  { backgroundColor: palette.primary || '#FF0600' }
                ]}
                textStyle={styles.submitButtonText}
              />
            </View>
          </>
        ) : (
          <>
            {/* Result Screen */}
            <View style={styles.resultContainer}>
              {/* Character Image with Feedback */}
              <View style={styles.characterContainer}>
                <Image 
                  source={getCharacterImage()}
                  style={styles.characterImage}
                  resizeMode="contain"
                />
              </View>
              
              <Text style={[styles.resultTitle, { color: '#1F2937' }]}>
                {rskAwarded > 0 ? 'Great Choice!' : 'Not Quite...'}
              </Text>
              
              <Text style={[styles.resultMessage, { color: '#6B7280' }]}>
                {selectedAnswer.explanation || 'Keep learning and making better decisions!'}
              </Text>

              <View style={styles.rskRewardContainer}>
                <RSKCurrency 
                  amount={Math.abs(rskAwarded)} 
                  size="large"
                  showLabel={true}
                />
                <Text style={[
                  styles.rskRewardText,
                  { color: getRskColor(rskAwarded) }
                ]}>
                  {rskAwarded > 0 ? 'Earned!' : 'Lost!'}
                </Text>
              </View>

              <AnimatedButton
                title="Continue"
                onPress={handleClose}
                style={[
                  styles.continueButton,
                  { backgroundColor: palette.primary || '#FF0600' }
                ]}
                textStyle={styles.continueButtonText}
              />
            </View>
          </>
        )}
      </View>
      </ScrollView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
    maxHeight: '90vh', // Limit height on web
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: 20,
  },
  container: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
  },
  characterContainer: {
    alignItems: 'center',
    marginBottom: 16,
  },
  characterImage: {
    width: 80,
    height: 80,
  },
  header: {
    marginBottom: 24,
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontFamily: 'Archivo',
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 12,
  },
  question: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '500',
    textAlign: 'center',
    lineHeight: 26,
  },
  answersContainer: {
    gap: 16,
    marginBottom: 32,
  },
  answerOption: {
    padding: 20,
    borderRadius: 12,
    borderWidth: 3,
    borderColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        boxShadow: '3px 4px 0px rgba(0,0,0,0.15)',
      },
    }),
  },
  answerHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    marginRight: 16,
    marginTop: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioButtonInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  answerTextContainer: {
    flex: 1,
  },
  answerText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '500',
    lineHeight: 22,
    marginBottom: 8,
  },
  rskIndicator: {
    alignSelf: 'flex-start',
  },
  rskText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  actions: {
    alignItems: 'center',
  },
  answerOptionSelected: {
    borderColor: '#FF4747',
    backgroundColor: '#FFF5F5',
    ...Platform.select({
      web: {
        boxShadow: '3px 4px 0px rgba(255,71,71,0.5)',
      },
    }),
  },
  submitButton: {
    minWidth: 200,
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 12,
    borderWidth: 3,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '4px 4px 0px rgba(0,0,0,0.85)',
      },
    }),
  },
  submitButtonText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '600',
    color: 'white',
  },
  resultContainer: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  resultTitle: {
    fontSize: 24,
    fontFamily: 'Archivo',
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 16,
  },
  resultMessage: {
    fontSize: 16,
    fontFamily: 'Archivo',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 32,
    paddingHorizontal: 20,
  },
  rskRewardContainer: {
    alignItems: 'center',
    marginBottom: 32,
    gap: 8,
  },
  rskRewardText: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  continueButton: {
    minWidth: 250,
    paddingVertical: 16,
    paddingHorizontal: 40,
    borderRadius: 12,
    borderWidth: 3,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '4px 4px 0px rgba(0,0,0,0.85)',
      },
    }),
  },
  continueButtonText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '600',
    color: 'white',
  },
  reviewIndicator: {
    width: 20,
    height: 20,
    borderRadius: 10,
    marginRight: 16,
    marginTop: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  reviewIndicatorText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: 'white',
  },
  reviewLabelsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 4,
  },
  reviewLabel: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  selectedLabel: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
});

export default DailyDecisionPopup;